"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { motion } from "framer-motion";
import ApplicationForm from "@/components/componentsV2/application-form/ApplicationForm";
import { PrismicNextImage } from "@prismicio/next";

/**
 * Props for `ApplyForm`.
 */
export type ApplyFormProps = SliceComponentProps<Content.ApplyFormSlice>;

/**
 * Component for "ApplyForm" Slices.
 */
const ApplyForm: FC<ApplyFormProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white py-[60px] px-4 md:px-[30px] lg:px-[60px] xl:px-20 flex flex-col items-center justify-center gap-4"
    >
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <PrismicRichText
          field={slice.primary.title}
          components={{
            label: ({ node, children }) => {
              if (node.data.label === "Highlight") {
                return <span className="text-secondary">{children}</span>;
              }
              return <span className="text-primary">{children}</span>;
            },
            heading1: ({ children }) => (
              <h1 className="font-archivo text-[#033D61] text-4xl text-center font-bold leading-12 md:text-5xl md:font-semibold md:leading-16">
                {children}
              </h1>
            ),
            heading2: ({ children }) => (
              <h2 className="font-archivo text-[#033D61] text-4xl text-center font-bold leading-12 md:text-5xl md:font-semibold md:leading-16">
                {children}
              </h2>
            ),
            heading3: ({ children }) => (
              <h3 className="font-archivo text-[#033D61] text-4xl text-center font-bold leading-12 md:text-5xl md:font-semibold md:leading-16">
                {children}
              </h3>
            ),
            heading4: ({ children }) => (
              <h4 className="font-archivo text-[#033D61] text-4xl text-center font-bold leading-12 md:text-5xl md:font-semibold md:leading-16">
                {children}
              </h4>
            ),
            heading5: ({ children }) => (
              <h5 className="font-archivo text-[#033D61] text-4xl text-center font-bold leading-12 md:text-5xl md:font-semibold md:leading-16">
                {children}
              </h5>
            ),
            heading6: ({ children }) => (
              <h6 className="font-archivo text-[#033D61] text-4xl text-center font-bold leading-12 md:text-5xl md:font-semibold md:leading-16">
                {children}
              </h6>
            ),
          }}
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <PrismicRichText
          field={slice.primary.sub_title}
          components={{
            label: ({ node, children }) => {
              if (node.data.label === "Highlight") {
                return <span className="text-secondary">{children}</span>;
              }
              return <span className="text-primary">{children}</span>;
            },
            paragraph: ({ children }) => (
              <p className="md:text-center text-[#033D61] font-merriweather font-normal leading-8 text-xl text-left mb-12 max-w-[908px]">{children}</p>
            )
          }}
        />
      </motion.div>
      <motion.div
        className="flex items-start justify-between flex-col xl:flex-row gap-[68px] w-full max-w-7xl"
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="space-y-2.5">
          <div className="flex items-center gap-[21px]">
            <div className="h-12 w-12 shrink-0 rounded-full bg-[#EBF7FF] flex items-center justify-center">
              <PrismicNextImage field={slice.primary.requirement_icon} />
            </div>
            <div className="text-[#033D61] font-archivo text-2xl font-semibold leading-9">
              {slice.primary.requirement_title}
            </div>
          </div>
          <div className="text-[#033D61] font-merriweather text-lg font-normal leading-7 pl-4">
            <PrismicRichText
              field={slice.primary.requirement_content}
              components={{
                label: ({ node, children }) => {
                  if (node.data.label === "Highlight") {
                    return <span className="text-secondary">{children}</span>;
                  }
                  return <span className="text-primary">{children}</span>;
                },
                list: ({ node, children }) => {
                  return (
                    <div className="list-disc list-inside">{children}</div>
                  );
                },
                oList: ({ node, children }) => {
                  return (
                    <div className="list-decimal list-inside">{children}</div>
                  );
                },
              }}
            />
          </div>
        </div>
        <ApplicationForm
          buttonLabel={slice.primary.form_button_label ?? ""}
          subtitle={slice.primary.form_sub_title ?? ""}
          title={slice.primary.form_title ?? ""}
          footerContent={slice.primary.form_footer ?? ""}
        />
      </motion.div>
    </section>
  );
};

export default ApplyForm;
